import React from 'react';
import { Firebase, FirestoreDb, useFirebase, useFirestoreDb } from '../Firebase';
import { MenuState } from './model/Menu';
import { MenuHeaderState } from './model/MenuHeader';
import { Loadable } from '../util/Loadable';


export function useMenu(): Loadable<MenuState> {
    const [state, setState] = React.useState<Loadable<MenuState>>(Loadable.empty());
    const triggerLoading = React.useRef(true);
    const db = useFirestoreDb();
    const fb = useFirebase();

    const get = React.useCallback((fb: Firebase, db: FirestoreDb) => {
        if (triggerLoading.current) {
            triggerLoading.current = false;
            const load = async () => {
                console.log('Start loading menu');
                setTimeout(() => setState(Loadable.startLoading()));
                const menuState = await loadMenu(fb, db);
                setTimeout(() => setState(menuState));
                console.log('Finished loading menu: ', menuState);
            };
            load();
        }
        return state;
    }, [state, setState]);

    return get(fb, db);
}

export function useMenuHeader(): Loadable<MenuHeaderState> {
    const [state, setState] = React.useState<Loadable<MenuHeaderState>>(Loadable.empty());
    const triggerLoading = React.useRef(true);
    const fb = useFirestoreDb();

    const get = React.useCallback((fb: FirestoreDb) => {
        if (triggerLoading.current) {
            triggerLoading.current = false;
            const load = async () => {
                console.log('Start loading menu header');
                setTimeout(() => setState(Loadable.startLoading()));
                const menuHeader = await loadMenuHeader(fb);
                setTimeout(() => setState(menuHeader));
                console.log('Finished loading menu header: ', menuHeader);
            };
            load();
        }
        return state;
    }, [state, setState]);

    return get(fb);
}

async function loadMenu(fb: Firebase, db: FirestoreDb): Promise<Loadable<MenuState>> {
    try {
        fb.logEvent('menu.load.request');

        const snapshot = await db.getRawMenuRef().once('value');
        if (!snapshot.exists()) {
            console.error('Menu not found');
            throw new Error('Menu not found');
        }
        fb.logEvent('menu.load.success');
        return Loadable.finishLoading({ obj: snapshot.val() });
    } catch (error) {
        fb.logEvent('menu.load.error');
        return Loadable.loadingError(error);
    }
}

async function loadMenuHeader(fb: FirestoreDb): Promise<Loadable<MenuHeaderState>> {
    try {
        const snapshot = await fb.getMenuHeaderRoot().get();
        return Loadable.finishLoading({ obj: snapshot.data()?.data ?? {} })
    } catch (error) {
        return Loadable.loadingError(error);
    }
}